/**
 * CptCardContainer
 *
 * Container for the CPT mismatch card variant. Similar to AuditRuleCardContainer
 * but handles CPT-specific actions — the CPT update does NOT go through the AI
 * chat flow, so this container manages the full lifecycle directly (no middleware).
 */

import React, { useMemo, useRef } from 'react';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks/core';

import type { CptAnalysisData } from '../../hooks/useAuditIssues';
import {
  resetCardState,
  setCardCompleted,
  setCardIgnored,
  startCardUpdate,
} from '../../store/auditUiSlice';
import {
  makeSelectCardCompletedVia,
  makeSelectCardStatus,
} from '../../store/selectors';
import type { BillingSupport } from '../../types';
import { mapBillingToCardData } from '../../utils/mapCardData';
import { AuditRuleCard } from '../cards';

const CPT_RULE_ID = 'cpt-mismatch';

interface CptCardContainerProps {
  billingSupport: BillingSupport;
  cptAnalysis: CptAnalysisData;
  noteId: string;
  encounterId?: string;
  onUseSuggestedCodes: (codes: string[]) => void;
}

export const CptCardContainer: React.FC<CptCardContainerProps> = ({
  billingSupport,
  cptAnalysis,
  noteId,
  encounterId,
  onUseSuggestedCodes,
}) => {
  const dispatch = useAppDispatch();
  const { noteAudit } = useEventTracking();

  // Store original code for undo
  const originalCodeRef = useRef<string | null>(cptAnalysis.providerCptCodes?.[0] ?? null);

  const selectStatus = useMemo(() => makeSelectCardStatus(CPT_RULE_ID), []);
  const selectCompletedVia = useMemo(() => makeSelectCardCompletedVia(CPT_RULE_ID), []);
  const cardStatus = useAppSelector(selectStatus);
  const cardCompletedVia = useAppSelector(selectCompletedVia);

  const cardData = useMemo(
    () => mapBillingToCardData(billingSupport, cptAnalysis),
    [billingSupport, cptAnalysis],
  );

  // Don't render if no mismatch
  if (!cardData) return null;

  const suggestedCode = billingSupport.proposed_primary_code;

  const handleApprove = () => {
    if (!suggestedCode) return;
    originalCodeRef.current = cptAnalysis.providerCptCodes?.[0] ?? null;
    dispatch(startCardUpdate({ ruleId: CPT_RULE_ID, preFixSectionIds: [], preFixSnapshotKeys: [] }));
    noteAudit.cptApproved({
      note_id: noteId,
      encounter_id: encounterId,
      codes_approved: [suggestedCode],
      target_cpt_source: cptAnalysis.targetCptSource ?? undefined,
    });
    onUseSuggestedCodes([suggestedCode]);
    dispatch(setCardCompleted({ ruleId: CPT_RULE_ID, affectedSectionIds: [], completedVia: 'ai' }));
  };

  const handleIgnore = () => {
    dispatch(setCardIgnored(CPT_RULE_ID));
    noteAudit.ruleDismissed({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: CPT_RULE_ID,
      compliance_status: 'no',
    });
  };

  const handleUndo = () => {
    noteAudit.ruleUndone({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: CPT_RULE_ID,
      previous_status: cardStatus as 'completed' | 'ignored',
      completed_via: cardCompletedVia,
    });
    if (cardCompletedVia === 'ai' && originalCodeRef.current) {
      onUseSuggestedCodes([originalCodeRef.current]);
    }
    dispatch(resetCardState(CPT_RULE_ID));
  };

  const isUndoable = cardStatus === 'completed' || cardStatus === 'ignored';

  return (
    <AuditRuleCard
      data={cardData}
      status={cardStatus}
      completedVia={cardCompletedVia}
      onApprove={cardStatus === 'idle' ? handleApprove : undefined}
      onIgnore={handleIgnore}
      onUndo={isUndoable ? handleUndo : undefined}
      expandMode="modal"
    />
  );
};
